import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Star, Lock, Download, XCircle, BookOpen, Trophy, Users, Clock, PlayCircle, Activity, MessageSquare, TrendingUp, ChevronDown, ArrowRight } from 'lucide-react';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/ki8DMGZw5wjahsMK1faLuo/Mockup_basketball_coach_ready.png';
// Basketball practice photo, downscaled + compressed for fast mobile loading
const HERO_BG = 'https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&w=1200&q=55';
// Mastercard interlocking-circles mark
const MastercardLogo = () => <span className="relative flex items-center" aria-label="Mastercard" data-id="element-3">
    <span className="block w-4 h-4 rounded-full bg-[#EB001B]" data-id="element-4" />
    <span className="block w-4 h-4 rounded-full bg-[#F79E1B] -ml-1.5 mix-blend-multiply" data-id="element-5" />
  </span>;
const PAYMENT_METHODS: Array<{
  label?: string;
  className?: string;
  logo?: 'mastercard';
}> = [{
  label: 'VISA',
  className: 'text-blue-800 italic font-extrabold'
}, {
  logo: 'mastercard'
}, {
  label: 'AMEX',
  className: 'text-blue-600 font-extrabold'
}, {
  label: ' Pay',
  className: 'text-black font-extrabold'
}, {
  label: 'G Pay',
  className: 'text-gray-700 font-extrabold'
}];
// Reusable payment-method logos — all in a single horizontal row
const PaymentBadges = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex flex-row flex-nowrap items-center justify-center gap-1.5 sm:gap-2 ${className}`} data-id="element-6">
    {PAYMENT_METHODS.map((m, i) => <div key={i} className="bg-white rounded-md px-2 py-1.5 sm:px-3 flex items-center justify-center shadow-md shrink-0" data-id="element-7">
        {m.logo === 'mastercard' ? <MastercardLogo data-id="element-8" /> : <span className={`text-[11px] sm:text-sm leading-none tracking-tight whitespace-nowrap ${m.className}`} data-id="element-9">
            {m.label}
          </span>}
      </div>)}
  </div>;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-10">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://basketballready.impultienda.ar/checkout';
const CTAButton = ({
  className = '',
  href
}: {
  className?: string;
  href?: string;
}) => <button onClick={() => {
  if (href) {
    window.location.href = href;
  } else {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  }
}} className={`group relative inline-flex items-center justify-center px-8 py-4 font-heading text-xl font-bold tracking-wide text-white transition-all duration-200 bg-brand-orange rounded-md hover:bg-orange-600 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 focus:ring-offset-brand-dark shadow-[0_0_20px_rgba(249,115,22,0.4)] hover:shadow-[0_0_30px_rgba(249,115,22,0.6)] ${className}`} data-id="element-11">
    GET INSTANT ACCESS NOW
    <ArrowRight className="ml-2 w-6 h-6 transition-transform group-hover:translate-x-1" data-id="element-12" />
  </button>;
// Thematic banner images (3:1) — basketball/coaching themed, optimized for fast loading
const VOLUME_IMAGES = ['https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1574623452334-1e0ac2b3ccb4?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1518063319789-7217e6706b04?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1593079831268-3381b0db4a77?auto=format&fit=crop&w=600&q=55' // analytics / film
];
const BONUS_IMAGES = ['https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1574623452334-1e0ac2b3ccb4?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&w=600&q=55', 'https://images.unsplash.com/photo-1518063319789-7217e6706b04?auto=format&fit=crop&w=600&q=55' // BONUS 5 — basketball player
];
// Compact card with 3:1 banner (image + number/title). Click the banner/title
// to expand the full description downward (accordion — not a carousel).
const ItemCard = ({
  kindLabel,
  num,
  title,
  pages,
  image,
  desc,
  freeTag = false
}: {
  kindLabel: string;
  num: string;
  title: string;
  pages: string;
  image: string;
  desc: string;
  freeTag?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return <div className="bg-brand-dark rounded-xl border border-white/5 hover:border-brand-orange/50 hover:shadow-[0_10px_30px_rgba(249,115,22,0.15)] transition-all duration-300 h-full overflow-hidden group" data-id="element-13">
      {/* 3:1 banner — acts as the accordion toggle */}
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="relative block w-full aspect-[3/1] overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange" data-id="element-14">
        <img src={image} alt={title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/70 to-brand-dark/20" data-id="element-16"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-4 sm:px-5" data-id="element-17">
          <div className="flex items-center gap-2 mb-1" data-id="element-18">
            <span className="font-heading font-bold text-brand-orange text-sm sm:text-base tracking-wider drop-shadow" data-id="element-19">
              {kindLabel} {num}
            </span>
            {freeTag && <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider" data-id="element-20">
                Free Today
              </span>}
          </div>
          <h3 className="font-heading font-bold uppercase leading-tight text-white text-lg sm:text-2xl max-w-[80%] drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]" data-id="element-21">
            {title}
          </h3>
        </div>
        {/* Expand indicator */}
        <span className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center" data-id="element-22">
          <ChevronDown className={`w-4 h-4 text-white transition-transform duration-300 ${open ? '-rotate-180' : ''}`} data-id="element-23" />
        </span>
      </button>

      {/* Compact footer + expandable description */}
      <div className="px-4 sm:px-5 py-3" data-id="element-24">
        <div className="flex items-center justify-between gap-3" data-id="element-25">
          <span className="text-[10px] sm:text-xs font-bold tracking-widest text-brand-orange uppercase" data-id="element-26">
            {pages}
          </span>
          <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="text-xs font-semibold text-gray-400 hover:text-brand-orange transition-colors" data-id="element-27">
            {open ? 'Hide details' : 'View details'}
          </button>
        </div>

        <AnimatePresence initial={false} data-id="element-28">
          {open && <motion.div key="content" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-29">
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed pt-3 border-t border-white/5 mt-3" data-id="element-30">
                {desc}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
};
export function LandingPage() {
  return <div className="overflow-x-hidden" data-id="element-31">
      {/* HEADER / HERO */}
      <header className="relative pt-10 pb-14 sm:pt-12 sm:pb-20 lg:pt-16 lg:pb-28 overflow-hidden" data-id="element-32">
        {/* Photographic basketball practice background — semi-blurred */}
        <div className="absolute inset-0 bg-cover bg-center -z-20 blur-[5px] scale-110" style={{
        backgroundImage: `url(${HERO_BG})`
      }} data-id="element-33"></div>
        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-dark/85 via-brand-dark/90 to-brand-dark" data-id="element-34"></div>
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(249,115,22,0.18),_transparent_60%)]" data-id="element-35"></div>

        <div className="container mx-auto px-4 max-w-3xl" data-id="element-36">
          <div className="flex flex-col items-center text-center z-10" data-id="element-37">
            {/* Main title */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-3xl sm:text-5xl lg:text-7xl font-bold leading-tight tracking-tight mb-6 sm:mb-10" data-id="element-38">
              Your Entire Basketball Coaching{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange to-orange-400" data-id="element-39">
                Year Solved
              </span>
            </motion.h1>

            {/* Product mockup with embedded subtitle */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="relative w-4/5 mx-auto" data-id="element-40">
              <div className="absolute inset-0 bg-brand-orange/20 blur-[100px] rounded-full" data-id="element-41"></div>
              <img src={HERO_IMAGE} alt="Basketball Coach Ready Bundle" className="relative z-10 w-full h-auto drop-shadow-2xl hover:scale-[1.02] transition-transform duration-500" data-id="element-42" />
            </motion.div>

            {/* Dual-color subtitle below the image */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.25
          }} className="mt-4 sm:mt-6 font-heading font-bold uppercase tracking-tight leading-none text-base sm:text-xl lg:text-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]" data-id="element-43">
              <span className="text-white" data-id="element-44">Stop Improvising </span>
              <span className="text-brand-orange" data-id="element-45">Every Practice.</span>
            </motion.p>

            {/* Supporting paragraph */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.35
          }} className="mt-6 sm:mt-10 sm:text-lg lg:text-xl text-gray-300 max-w-2xl mx-auto text-[14px]" data-id="element-46">
              <strong className="text-white" data-id="element-47">Basketball Coach Ready</strong> —
              10 volumes and 5 bonuses bundle. 700+ pages, 10 complete coaching
              volumes, 5 powerful bonuses and everything an American basketball
              coach needs — prepared in minutes, executed with confidence.
            </motion.p>

            {/* Feature chips */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.45
          }} className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-300 font-medium mt-6 sm:mt-8" data-id="element-48">
              <div className="flex items-center gap-2" data-id="element-49">
                <Download className="w-4 h-4 text-sky-400" data-id="element-50" />
                Digital PDF
              </div>
              <span className="text-gray-600 hidden sm:inline" data-id="element-51">•</span>
              <div className="flex items-center gap-2" data-id="element-52">
                <Clock className="w-4 h-4 text-sky-400" data-id="element-53" />
                Instant Access
              </div>
              <span className="text-gray-600 hidden sm:inline" data-id="element-54">•</span>
              <div className="flex items-center gap-2" data-id="element-55">
                <ShieldCheck className="w-4 h-4 text-sky-400" data-id="element-56" />
                10 Volumes + 5 Free Bonuses
              </div>
            </motion.div>

            {/* Full-width CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.55
          }} className="w-full max-w-xl mt-7 sm:mt-10" data-id="element-57">
              <CTAButton className="w-full text-lg sm:text-xl lg:text-2xl py-4 sm:py-5" data-id="element-58" />
            </motion.div>

            {/* Payment method badges */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.65
          }} className="mt-6 sm:mt-8" data-id="element-59">
              <PaymentBadges data-id="element-60" />
            </motion.div>

            {/* Trust badges */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.75
          }} className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400 font-medium mt-6 sm:mt-8" data-id="element-61">
              <div className="flex items-center gap-2" data-id="element-62">
                <Lock className="w-4 h-4 text-brand-orange" data-id="element-63" />
                Secure Payment
              </div>
              <div className="flex items-center gap-2" data-id="element-64">
                <ShieldCheck className="w-4 h-4 text-brand-orange" data-id="element-65" />
                7-Day Guarantee
              </div>
            </motion.div>

            {/* Rating */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.85
          }} className="mt-6 sm:mt-8 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base" data-id="element-66">
              <div className="flex text-yellow-500" data-id="element-67">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-current" data-id="element-68" />)}
              </div>
              <span className="text-white font-bold" data-id="element-69">4.9/5</span>
              <span className="text-gray-400" data-id="element-70">
                — +1,000 coaches across the USA 🇺🇸
              </span>
            </motion.div>
          </div>
        </div>
      </header>

      {/* PAIN POINTS */}
      <section className="py-12 sm:py-20 bg-brand-gray border-y border-white/5" data-id="element-71">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-72">
          <FadeIn data-id="element-73">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-8 sm:mb-12" data-id="element-74">
              IF EVERY WEEK YOU LOSE HOURS PLANNING YOUR PRACTICES,{' '}
              <span className="text-brand-orange" data-id="element-75">
                THIS WILL SOUND FAMILIAR:
              </span>
            </h2>
          </FadeIn>

          <div className="space-y-3 sm:space-y-4" data-id="element-76">
            {['You spend too much time searching for drills or inventing practice plans at the last minute — right before tip-off', 'You have no clear player development progression by level and end up running the same drills every single practice', 'You walk into practice without a fluid structure and waste the first 10 minutes figuring out what comes next', 'You have no serious library of plays, sessions and coaching protocols to pull from without starting from scratch every week', 'When the season gets intense, your planning falls apart and you improvise on the sideline more than you should', "You want to look more prepared and professional in front of your players, parents and athletic director — but without a ready system you waste hours you don't have", 'You have no reliable system to track player development and show real progress to parents and administration', 'You feel unprepared on game day and know your team could perform better with a smarter coaching system'].map((point, i) => <FadeIn key={i} delay={i * 0.06} data-id="element-77">
                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-brand-dark/50 border border-white/5 hover:border-red-500/40 hover:bg-brand-dark/80 transition-all duration-200" data-id="element-78">
                  <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 shrink-0 mt-0.5" data-id="element-79" />
                  <p className="text-gray-300 text-base sm:text-lg" data-id="element-80">{point}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* SOLUTION INTRO */}
      <section className="py-14 sm:py-24 relative overflow-hidden" data-id="element-81">
        <div className="container mx-auto px-4 max-w-4xl text-center" data-id="element-82">
          <FadeIn data-id="element-83">
            {/* Duplicated product mockup, scaled to 80% */}
            <div className="relative w-4/5 mx-auto mb-8 sm:mb-12" data-id="element-84">
              <div className="absolute inset-0 bg-brand-orange/20 blur-[100px] rounded-full" data-id="element-85"></div>
              <img src={HERO_IMAGE} alt="Basketball Coach Ready Bundle" loading="lazy" className="relative z-10 w-full h-auto drop-shadow-2xl" data-id="element-86" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6" data-id="element-87">
              THE <span className="text-brand-orange" data-id="element-88">COMPLETE</span> SYSTEM
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-6 sm:mb-12" data-id="element-89">
              Ten comprehensive volumes — over 700 pages of pure coaching
              content — with everything you need to prepare faster, coach
              smarter and arrive at every practice and game day completely
              ready.
            </p>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-90">
              The foundation of everything. Ten volumes of pure coaching gold.
              Stop reacting to whatever your players need in the moment and
              start building a program that truly transforms them — from youth
              all the way to varsity level.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 10 VOLUMES */}
      <section className="py-12 sm:py-20 bg-brand-gray" data-id="element-91">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-92">
          <FadeIn data-id="element-93">
            <div className="text-center mb-8 sm:mb-16" data-id="element-94">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-id="element-95">
                THE COMPLETE 10-VOLUME SYSTEM
              </h2>
              <div className="w-24 h-1 bg-brand-orange mx-auto" data-id="element-96"></div>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8" data-id="element-97">
            {[{
            num: '01',
            title: 'THE AMERICAN COACHING SYSTEM',
            desc: 'The complete framework for building a professional basketball program in the USA. Coaching philosophy, season structure, program culture and the roadmap every American coach needs from day one.',
            icon: <BookOpen className="w-8 h-8" data-id="element-98" />
          }, {
            num: '02',
            title: 'OFFENSIVE SYSTEMS BLUEPRINT',
            desc: '50 complete offensive plays fully diagrammed — motion offense, pick & roll systems, BLOB plays, SLOB plays, last-second sets and full fastbreak transition packages. Your offense, built and ready.',
            icon: <Activity className="w-8 h-8" data-id="element-99" />
          }, {
            num: '03',
            title: 'DEFENSIVE DOMINATION',
            desc: 'The complete American defense manual. Man-to-man, 2-3 Zone, 1-3-1, press defense packages, help rotations, clutch situation defense and how to build a defensive identity your team owns.',
            icon: <ShieldCheck className="w-8 h-8" data-id="element-100" />
          }, {
            num: '04',
            title: 'PLAYER DEVELOPMENT SYSTEM',
            desc: 'Individual development programs by position — Point Guard, Shooting Guard, Small Forward, Power Forward and Center. From bench player to scholarship prospect, with measurable progression at every level.',
            icon: <TrendingUp className="w-8 h-8" data-id="element-101" />
          }, {
            num: '05',
            title: '250+ SESSION LIBRARY',
            desc: 'Every practice done for you. 250+ complete practice sessions organized by level: Youth (U10–U12), Middle School (U13–U15), High School (U16–U18) and Advanced Varsity. Every session includes objective, timing and variations.',
            icon: <Clock className="w-8 h-8" data-id="element-102" />
          }, {
            num: '06',
            title: '150 DRILLS ENCYCLOPEDIA',
            desc: '150 drills categorized and ready to run: 50 shooting drills, 40 ball handling drills, 30 defensive drills and 30 conditioning drills. Every drill includes setup, coaching points, progressions and difficulty level.',
            icon: <PlayCircle className="w-8 h-8" data-id="element-103" />
          }, {
            num: '07',
            title: 'GAME DAY MASTERY',
            desc: 'Everything that happens before, during and after the game. Pre-game routines, timeout management, halftime adjustments, live substitution systems and ATO plays that win close games.',
            icon: <Trophy className="w-8 h-8" data-id="element-104" />
          }, {
            num: '08',
            title: 'STRENGTH, CONDITIONING & INJURY PREVENTION',
            desc: 'Basketball-specific athletic development. Strength and explosiveness programs, speed and agility protocols, the 5 most common basketball injuries and how to prevent them, plus recovery systems for a full season.',
            icon: <Activity className="w-8 h-8" data-id="element-105" />
          }, {
            num: '09',
            title: 'TEAM CULTURE, LEADERSHIP & PARENT MGT',
            desc: 'The off-court winning formula. How to build a winning program culture, manage difficult parents, handle star players vs. role players, communicate with your athletic director and run a program parents trust.',
            icon: <Users className="w-8 h-8" data-id="element-106" />
          }, {
            num: '10',
            title: 'SCOUTING, FILM STUDY & ANALYTICS',
            desc: 'See the game before it happens. Rival scouting frameworks, film study systems used at high school and college level, basic analytics every coach must know, and free tools — Hudl, FastModel, Synergy — explained simply.',
            icon: <Activity className="w-8 h-8" data-id="element-107" />
          }].map((vol, i) => <FadeIn key={i} delay={i * 0.04} data-id="element-108">
                <ItemCard kindLabel="VOL" num={vol.num} title={vol.title} pages="80–100 pages" image={VOLUME_IMAGES[i]} desc={vol.desc} data-id="element-109" />
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-14 sm:py-24 relative" data-id="element-110">
        <div className="absolute inset-0 bg-brand-orange/5" data-id="element-111"></div>
        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-112">
          <FadeIn data-id="element-113">
            <div className="text-center mb-8 sm:mb-16" data-id="element-114">
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 text-brand-orange" data-id="element-115">
                PLUS 5 FREE BONUSES TODAY
              </h2>
              <p className="text-base sm:text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-116">
                We've packed an additional 300+ pages of high-value resources to
                ensure you have absolutely everything you need to run a
                professional program starting tomorrow.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6" data-id="element-117">
            {[{
            num: '01',
            title: 'THE AMERICAN SEASON PLANNER',
            desc: 'A complete 12-month basketball calendar built around the US school year and AAU season. Pre-season, regular season, playoffs and off-season planning — all done for you with weekly planning sheets and practice templates.'
          }, {
            num: '02',
            title: 'THE RECRUITING TOOLKIT',
            desc: 'How to get your players seen by college scouts. Player profile templates, NCAA vs. NAIA vs. JUCO breakdown, scripts for contacting college coaches and a recruiting event calendar for USA coaches. No other basketball product on the market includes this.'
          }, {
            num: '03',
            title: '200 WARM-UP & ACTIVATION ROUTINES',
            desc: '80 warm-up routines, 60 cool-down and recovery routines, and 60 pre-game activation protocols — all organized by age group and situation, with estimated timing and variations for every session.'
          }, {
            num: '04',
            title: "THE COACH'S COMMUNICATION ARSENAL",
            desc: 'Scripts for difficult parent conversations, welcome emails, program expectation letters, parent meeting templates and how to communicate playing time decisions without drama. The off-court tool every coach needs.'
          }, {
            num: '05',
            title: 'PLAYER EVALUATION MASTER SYSTEM',
            desc: 'Complete evaluation sheets by position, full-season progress trackers, professional parent report templates, tryout evaluation rubrics and individual player development plan templates. Track every player, every season.'
          }].map((bonus, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-118">
                <ItemCard kindLabel="BONUS" num={bonus.num} title={bonus.title} pages="60–80 pages" image={BONUS_IMAGES[i]} desc={bonus.desc} freeTag data-id="element-119" />
              </FadeIn>)}
          </div>

          <FadeIn delay={0.5} data-id="element-120">
            <div className="mt-8 sm:mt-12 text-center p-5 sm:p-8 bg-brand-orange/10 rounded-2xl border border-brand-orange/30" data-id="element-121">
              <h3 className="text-xl sm:text-2xl font-bold mb-2" data-id="element-122">
                Total Bonus Value:{' '}
                <span className="line-through text-gray-500" data-id="element-123">$214</span>
              </h3>
              <p className="text-base sm:text-xl text-brand-orange font-bold uppercase tracking-wide" data-id="element-124">
                TODAY FREE — Included in your system at no extra cost.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PRICING / OFFER */}
      <section id="pricing" className="py-14 sm:py-24 bg-brand-gray border-t border-white/10" data-id="element-125">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-126">
          <FadeIn data-id="element-127">
            <div className="text-center mb-8 sm:mb-12" data-id="element-128">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 text-red-400 font-bold text-xs sm:text-sm uppercase tracking-wider mb-4 sm:mb-6 animate-pulse" data-id="element-129">
                <Clock className="w-4 h-4" data-id="element-130" />
                Launch Price Ends Soon
              </div>
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4" data-id="element-131">
                UNLOCK BASKETBALL COACH READY TODAY
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-132">
            <div className="bg-brand-dark rounded-3xl border-2 border-brand-orange p-5 sm:p-8 lg:p-12 shadow-[0_0_50px_rgba(249,115,22,0.15)] relative overflow-hidden" data-id="element-133">
              <div className="absolute top-0 right-0 bg-brand-orange text-white font-bold py-1 px-10 transform translate-x-[30%] translate-y-[50%] rotate-45" data-id="element-134">
                88% OFF
              </div>

              <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 text-sm sm:text-base text-gray-300" data-id="element-135">
                {['Volume 1: The American Coaching System — $27', 'Volume 2: Offensive Systems Blueprint — $27', 'Volume 3: Defensive Domination — $27', 'Volume 4: Player Development System — $27', 'Volume 5: 250+ Session Library — $27', 'Volume 6: 150 Drills Encyclopedia — $27', 'Volume 7: Game Day Mastery — $27', 'Volume 8: Strength, Conditioning & Injury Prevention — $27', 'Volume 9: Team Culture, Leadership & Parent Management — $27', 'Volume 10: Scouting, Film Study & Analytics — $27', '5 Exclusive Free Bonuses — 300+ Pages — $97', 'Instant PDF Access — $18'].map((item, i) => <div key={i} className="flex items-center justify-between border-b border-white/5 pb-2" data-id="element-136">
                    <span className="flex items-center gap-2" data-id="element-137">
                      <CheckCircle2 className="w-4 h-4 text-brand-orange" data-id="element-138" />
                      {item.split('—')[0]}
                    </span>
                    <span className="text-gray-500 line-through" data-id="element-139">
                      {item.split('—')[1]}
                    </span>
                  </div>)}
              </div>

              <div className="text-center mb-6 sm:mb-8" data-id="element-140">
                <p className="text-gray-400 mb-2" data-id="element-141">
                  Real Value: <span className="line-through" data-id="element-142">$367</span>
                </p>
                <div className="text-6xl sm:text-7xl font-heading font-bold text-white mb-2" data-id="element-143">
                  $27
                </div>
                <p className="text-brand-orange font-bold uppercase tracking-wider text-sm sm:text-base" data-id="element-144">
                  One-time payment · Instant access
                </p>
              </div>

              <div className="flex flex-col items-center gap-6" data-id="element-145">
                <CTAButton className="w-full text-2xl py-5" href={CHECKOUT_URL} data-id="element-146" />
                <PaymentBadges data-id="element-147" />
                <p className="text-gray-400 text-center" data-id="element-148">
                  Everything you need to coach with confidence — starting today.
                </p>

                <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400" data-id="element-149">
                  <div className="flex items-center gap-2" data-id="element-150">
                    <Lock className="w-4 h-4" data-id="element-151" /> Secure Payment
                  </div>
                  <div className="flex items-center gap-2" data-id="element-152">
                    <ShieldCheck className="w-4 h-4" data-id="element-153" /> 7-Day Guarantee
                  </div>
                  <div className="flex items-center gap-2" data-id="element-154">
                    <CheckCircle2 className="w-4 h-4" data-id="element-155" /> Verified Purchase
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-14 sm:py-24" data-id="element-156">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-157">
          <FadeIn data-id="element-158">
            <div className="text-center mb-8 sm:mb-16" data-id="element-159">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-160">
                WHAT COACHES ARE ALREADY SAYING
              </h2>
              <div className="flex items-center justify-center gap-2 text-yellow-500 mb-2" data-id="element-161">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 sm:w-6 sm:h-6 fill-current" data-id="element-162" />)}
              </div>
              <p className="text-base sm:text-xl text-gray-400" data-id="element-163">
                4.9/5 — Trusted by coaches across the USA 🇺🇸
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-164">
            {[{
            text: 'I used to show up to practice with a rough idea and just wing it. Now I have 250 complete sessions ready to run, organized by level and objective. My players notice the difference every single day — and so do their parents.',
            name: 'Coach Michael R.',
            role: 'High School Varsity Coach — Chicago, IL',
            avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=120&q=60'
          }, {
            text: "I had some drills I liked but nothing organized or progressive. Basketball Coach Ready gave me a complete system I can actually trust. For the first time in three years I walk into every practice knowing exactly what we're doing and why.",
            name: 'Coach Sarah L.',
            role: 'AAU & Youth Basketball Coach — Atlanta, GA',
            avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=120&q=60'
          }, {
            text: 'The Player Development System alone was worth everything. I finally have a position-by-position roadmap that shows real, measurable progress. Parents are seeing results and my retention has never been better.',
            name: 'Coach Daniel M.',
            role: 'Youth Program Director — Houston, TX',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&q=60'
          }, {
            text: 'The bonuses are an absolute goldmine. The Communication Arsenal saved me from three nightmare parent conversations this season alone. I look and sound like a professional at every level — on and off the court.',
            name: 'Coach Jessica T.',
            role: 'Middle School Head Coach — Phoenix, AZ',
            avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=120&q=60'
          }, {
            text: 'The Game Day Mastery volume completely changed how I manage close games. The ATO plays, the timeout scripts, the halftime adjustment framework — we won two games this season we had no business winning. This is the real deal.',
            name: 'Coach Marcus B.',
            role: 'Varsity Head Coach — Dallas, TX',
            avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=120&q=60'
          }, {
            text: 'As a first-year coach I was completely overwhelmed. Basketball Coach Ready gave me a full professional system from day one. The season planner alone saved me probably 40 hours. I finally feel like I belong on that sideline.',
            name: 'Coach Andrea P.',
            role: 'First-Year Head Coach — Seattle, WA',
            avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=120&q=60'
          }].map((testimonial, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-165">
                <div className="bg-brand-gray p-5 sm:p-8 rounded-xl border border-white/5 hover:border-brand-orange/40 hover:-translate-y-1 transition-all duration-300 h-full flex flex-col" data-id="element-166">
                  <div className="flex text-yellow-500 mb-3 sm:mb-4" data-id="element-167">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-168" />)}
                  </div>
                  <p className="text-sm sm:text-base text-gray-300 italic mb-4 sm:mb-6 flex-1" data-id="element-169">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3" data-id="element-170">
                    <img src={testimonial.avatar} alt={testimonial.name} loading="lazy" className="w-11 h-11 rounded-full object-cover border-2 border-brand-orange/40 shrink-0" data-id="element-171" />
                    <div data-id="element-172">
                      <div className="font-bold text-white" data-id="element-173">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-brand-orange" data-id="element-174">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-24 bg-brand-gray" data-id="element-175">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-176">
          <FadeIn data-id="element-177">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 sm:mb-16" data-id="element-178">
              FREQUENTLY ASKED QUESTIONS
            </h2>
          </FadeIn>

          <div className="space-y-3 sm:space-y-6" data-id="element-179">
            {[{
            q: 'Is this suitable for beginner coaches or only experienced ones?',
            a: 'Basketball Coach Ready was designed to work for coaches at every level. Whether you are stepping onto the sideline for the first time or have years of experience, the ten volumes take you from foundational principles all the way through advanced game management and analytics. Each volume builds on the previous one, so you always have the right content for exactly where you are right now.'
          }, {
            q: 'Do I receive physical materials?',
            a: 'Basketball Coach Ready is a 100% digital product. After your purchase is confirmed, you receive instant access to all 10 volumes and 5 bonuses as downloadable PDF files. You can read them on your phone, tablet, laptop or print any pages you want to use on the bench.'
          }, {
            q: 'Can I read it on my phone or tablet?',
            a: 'Absolutely. All files are optimized for every device. Many coaches keep their session library open on their phone during practice and their play diagrams on a tablet on the bench. Everything is formatted for clean reading on any screen size.'
          }, {
            q: 'Is this a theory course or a certification program?',
            a: "Neither. Basketball Coach Ready is a complete done-for-you practical system. Every volume is built around tools you implement immediately — practice sessions, play diagrams, evaluation sheets, scripts and planners. Zero theory for theory's sake. Everything is actionable starting with your very next practice."
          }, {
            q: 'Will this actually help me prepare sessions faster?',
            a: 'That is exactly what it was built for. Instead of spending hours searching YouTube for drills, building plays from scratch or staring at a blank practice plan — you open Basketball Coach Ready and everything is already organized, progressive and ready to run. Most coaches report saving 5 to 10 hours every single week during the season.'
          }, {
            q: 'What is the 7-day guarantee?',
            a: 'Open everything. Read everything. Run a practice with it. If Basketball Coach Ready does not completely transform how you plan and run your program, email us within 7 days of purchase and we will refund every cent. No questions asked. Our refund rate is below 1% — because the system works.'
          }].map((faq, i) => <FadeIn key={i} delay={i * 0.06} data-id="element-180">
                <details className="group bg-brand-dark rounded-xl border border-white/5 hover:border-brand-orange/40 transition-colors [&_summary::-webkit-details-marker]:hidden" data-id="element-181">
                  <summary className="flex items-center justify-between gap-3 p-4 sm:p-6 cursor-pointer font-bold text-base sm:text-lg" data-id="element-182">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-brand-orange shrink-0 transition-transform group-open:-rotate-180" data-id="element-183" />
                  </summary>
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-sm sm:text-base text-gray-400 leading-relaxed border-t border-white/5 pt-3 sm:pt-4 mt-2" data-id="element-184">
                    {faq.a}
                  </div>
                </details>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 sm:py-24 relative overflow-hidden" data-id="element-185">
        <div className="absolute inset-0 bg-brand-orange/10" data-id="element-186"></div>
        <div className="container mx-auto px-4 max-w-4xl text-center relative z-10" data-id="element-187">
          <FadeIn data-id="element-188">
            <h2 className="text-3xl sm:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6" data-id="element-189">
              STOP IMPROVISING{' '}
              <span className="text-brand-orange" data-id="element-190">EVERY WEEK.</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-300 mb-4 max-w-2xl mx-auto" data-id="element-191">
              Basketball Coach Ready gives you the complete system to prepare
              every practice in minutes and coach with the professionalism your
              players, parents and athletic director deserve.
            </p>
            <p className="text-base sm:text-lg font-bold text-white mb-8 sm:mb-12" data-id="element-192">
              Less time staring at a blank practice sheet — more time actually
              coaching.
            </p>

            <div className="bg-brand-dark/80 backdrop-blur-sm p-5 sm:p-8 rounded-2xl border border-brand-orange/30 inline-block w-full max-w-xl mx-auto mb-6 sm:mb-8" data-id="element-193">
              <div className="text-center mb-6" data-id="element-194">
                <p className="text-gray-400 mb-1" data-id="element-195">
                  Real Value: <span className="line-through" data-id="element-196">$367</span>{' '}
                  <span className="text-brand-orange font-bold ml-2" data-id="element-197">
                    88% OFF
                  </span>
                </p>
                <div className="text-5xl sm:text-6xl font-heading font-bold text-white mb-1" data-id="element-198">
                  $27
                </div>
                <p className="text-gray-400 text-sm uppercase tracking-wider" data-id="element-199">
                  One-time payment · Instant access
                </p>
              </div>
              <CTAButton className="w-full text-lg sm:text-xl py-4 mb-4" href={CHECKOUT_URL} data-id="element-200" />
              <PaymentBadges className="mb-4" data-id="element-201" />
              <p className="text-sm text-gray-400" data-id="element-202">
                Everything you need to coach with confidence — starting today.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400" data-id="element-203">
              <div className="flex items-center gap-2" data-id="element-204">
                <Lock className="w-4 h-4" data-id="element-205" /> Secure Payment
              </div>
              <div className="flex items-center gap-2" data-id="element-206">
                <ShieldCheck className="w-4 h-4" data-id="element-207" /> 7-Day Guarantee
              </div>
              <div className="flex items-center gap-2" data-id="element-208">
                <CheckCircle2 className="w-4 h-4" data-id="element-209" /> Verified Purchase
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 sm:py-12 bg-black border-t border-white/10 text-center text-gray-500 text-sm" data-id="element-210">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-211">
          <p className="mb-4" data-id="element-212">
            © 2026 Basketball Coach Ready — All Rights Reserved. Unauthorized
            reproduction and redistribution strictly prohibited.
          </p>
          <p className="text-xs text-gray-600" data-id="element-213">
            Our products and content are strictly educational and informational
            in nature. Individual results vary and depend entirely on the
            commitment, skills and personal application of each user.
          </p>
        </div>
      </footer>
    </div>;
}